// Validadores de identificador tributario por país. Algoritmos públicos,
// sin dependencia de APIs externas. Cubre el "dígito verificador" — NO valida
// que el contribuyente realmente exista en el SII/AFIP/SAT (eso requiere API).
//
// Compartido entre apps/web y functions/.

export type TaxIdFormat = "CL-RUT" | "AR-CUIT" | "MX-RFC" | "PE-RUC" | "CO-NIT" | "generic";

export type TaxIdValidation = {
  valid: boolean;
  normalized: string;
  isLikelyBusiness: boolean;
  reason?: string;
};

/**
 * Punto de entrada principal. Despacha al validador del país correspondiente.
 */
export function validateTaxId(value: string, format: TaxIdFormat): TaxIdValidation {
  switch (format) {
    case "CL-RUT":
      return validateChileanRut(value);
    case "AR-CUIT":
      return validateArgentineCuit(value);
    default:
      // Genérico: solo chequea longitud mínima y caracteres válidos.
      return {
        valid: /^[A-Za-z0-9.\-]{4,30}$/.test(value.trim()),
        normalized: value.trim().toUpperCase(),
        isLikelyBusiness: false
      };
  }
}

/**
 * Chile RUT — algoritmo módulo 11.
 *
 * Formatos aceptados: "77.411.698-2", "77411698-2", "774116982", "77.411.698-K"
 * El último carácter es el dígito verificador (0-9 o K).
 *
 * `isLikelyBusiness`: en Chile los RUTs ≥ 50.000.000 son típicamente empresas
 * (RUTs corporativos asignados desde 1995). Heurística no perfecta (EIRL y
 * algunos extranjeros pueden estar por debajo), pero suficiente como gate
 * inicial. Verificación definitiva sigue siendo SII.
 */
export function validateChileanRut(input: string): TaxIdValidation {
  const cleaned = (input ?? "").replace(/[.\s]/g, "").toUpperCase();
  const m = cleaned.match(/^(\d{1,8})-?([0-9K])$/);
  if (!m) {
    return {
      valid: false,
      normalized: cleaned,
      isLikelyBusiness: false,
      reason: "Formato esperado: 12.345.678-9"
    };
  }
  const body = m[1];
  const dv = m[2];
  const calculated = computeChileanRutDv(body);
  if (calculated !== dv) {
    return {
      valid: false,
      normalized: formatChileanRut(body, dv),
      isLikelyBusiness: false,
      reason: "Dígito verificador no coincide"
    };
  }
  const numericBody = parseInt(body, 10);
  return {
    valid: true,
    normalized: formatChileanRut(body, dv),
    isLikelyBusiness: numericBody >= 50_000_000
  };
}

function computeChileanRutDv(body: string): string {
  const reversed = body.split("").reverse();
  const multipliers = [2, 3, 4, 5, 6, 7];
  let sum = 0;
  for (let i = 0; i < reversed.length; i++) {
    sum += parseInt(reversed[i], 10) * multipliers[i % multipliers.length];
  }
  const mod = 11 - (sum % 11);
  if (mod === 11) return "0";
  if (mod === 10) return "K";
  return String(mod);
}

function formatChileanRut(body: string, dv: string): string {
  const reversed = body.split("").reverse();
  const groups: string[] = [];
  for (let i = 0; i < reversed.length; i += 3) {
    groups.push(reversed.slice(i, i + 3).reverse().join(""));
  }
  return `${groups.reverse().join(".")}-${dv}`;
}

/**
 * Argentina CUIT/CUIL — algoritmo módulo 11.
 *
 * Formato: 11 dígitos, opcionalmente con guiones. Ej: "30-12345678-9" o "30123456789".
 * Los dos primeros dígitos indican tipo:
 *   - 30, 33, 34: persona jurídica → isLikelyBusiness true
 *   - 20, 23, 24, 27: persona física
 */
export function validateArgentineCuit(input: string): TaxIdValidation {
  const cleaned = (input ?? "").replace(/[-\s]/g, "");
  if (!/^\d{11}$/.test(cleaned)) {
    return {
      valid: false,
      normalized: cleaned,
      isLikelyBusiness: false,
      reason: "CUIT debe tener 11 dígitos"
    };
  }
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cleaned[i], 10) * weights[i];
  const mod = 11 - (sum % 11);
  const expected = mod === 11 ? 0 : mod === 10 ? 9 : mod;
  const dv = parseInt(cleaned[10], 10);
  if (dv !== expected) {
    return {
      valid: false,
      normalized: formatArgentineCuit(cleaned),
      isLikelyBusiness: false,
      reason: "Dígito verificador no coincide"
    };
  }
  const prefix = cleaned.slice(0, 2);
  const businessPrefixes = ["30", "33", "34"];
  return {
    valid: true,
    normalized: formatArgentineCuit(cleaned),
    isLikelyBusiness: businessPrefixes.includes(prefix)
  };
}

function formatArgentineCuit(cleaned: string): string {
  return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10)}`;
}

/** Helper para slug derivado de nombre comercial (acentos, espacios, etc.). */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}
